<script setup lang="ts">
import { IEvent } from "~/types/sports/event";
const router = useRouter();
const route = useRoute();
interface Props {
	selectedEventId: number | undefined;
	events: IEvent[] | undefined;
	isLoading?: boolean | false;
}

const props = defineProps<Props>();
const selectedEventMeta = computed(() => {
	return props.events?.find((e) => e.eventId === props.selectedEventId);
});
const relatedEvents = computed(() => {
	return props.events?.filter((e) => e.leagueId === selectedEventMeta.value?.leagueId);
});

onMounted(() => {
	setTimeout(() => {
		scrollToEvent();
	}, 800);
});

const scrollToEvent = () => {
	const item = document.getElementById(`${props.selectedEventId}`);
	if (item) {
		const parent = item.closest(".navigation") as HTMLElement;
		if (parent) {
			parent.scrollLeft = item.offsetLeft - parent.offsetLeft - 40;
		}
	}
};

const getTimeFromEpoch = (epoch: number) => {
	const date = new Date(epoch * 1000);
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	return `${hours}:${minutes}`;
};

const navigateToRace = (raceEvent: IEvent) => {
	router.push({ query: { ...route.query, eventId: raceEvent.eventId } });
};
</script>

<template>
	<div class="p-1 rounded-b-lg dark:text-light-50 font-bold bg-light-500 dark:bg-dark-600 mb-2">
		<div class="flex p-[2px] overflow-x-auto navigation">
			<div class="flex flex-nowrap items-center">
				<div v-if="isLoading" class="flex items-center justify-between">
					<UiSkeleton v-for="nav in 8" :key="`event-nav-${nav}`" class="w-[90px] mx-1 h-[40px] bg-light-50 dark:bg-dark-500 rounded-md" />
				</div>
				<template v-else>
					<div
						v-for="event in relatedEvents"
						:class="
							event.eventId === selectedEventId ? 'text-light-50 bg-primary-600' : 'bg-light-50 dark:bg-dark-700 text-dark-800 dark:text-light-50'
						"
						class="flex justify-center items-center mx-1 w-[90px] h-[40px] rounded-md cursor-pointer font-bold"
						@click="navigateToRace(event)"
						:id="`${event.eventId}`"
					>
						<div class="text-xs mr-2">
							<span v-if="event.venueEpoch">
								{{ getTimeFromEpoch(event.venueEpoch) }}
							</span>
							<span v-else>
								{{ getTimeFromEpoch(event.expectedStartEpoch) }}
							</span>
						</div>
						<svg v-if="!event.isActive" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 16" fill="none">
							<g clip-path="url(#clip0_6658_6357)">
								<path
									d="M13.835 6.91518C13.8347 5.6017 13.4605 4.31544 12.7561 3.20684C12.0516 2.09824 11.0461 1.21312 9.85709 0.655007C8.66808 0.0968938 7.34475 -0.111143 6.04185 0.0552272C4.73894 0.221597 3.51034 0.755497 2.49973 1.59448C1.48913 2.43347 0.738287 3.54286 0.335031 4.79291C-0.0682242 6.04295 -0.107225 7.38198 0.222589 8.65337C0.552404 9.92477 1.2374 11.076 2.19746 11.9724C3.15753 12.8688 4.35297 13.4733 5.64398 13.7152V15.9992H8.19398V13.7142C9.77945 13.4164 11.211 12.5739 12.2411 11.3324C13.2711 10.0908 13.835 8.52838 13.835 6.91518ZM6.91898 11.2572C6.05479 11.2574 5.20994 11.0013 4.49131 10.5213C3.77268 10.0413 3.21254 9.35896 2.88174 8.56058C2.55094 7.76221 2.46433 6.88367 2.63288 6.03607C2.80142 5.18848 3.21755 4.4099 3.82863 3.79883C4.4397 3.18775 5.21827 2.77163 6.06587 2.60308C6.91347 2.43453 7.79201 2.52114 8.59038 2.85194C9.38875 3.18274 10.0711 3.74288 10.5511 4.46152C11.0311 5.18015 11.2872 6.02499 11.287 6.88918C11.2867 8.04756 10.8264 9.15843 10.0073 9.97753C9.18822 10.7966 8.07736 11.2569 6.91898 11.2572Z"
									fill="#ED1C24"
								/>
							</g>
							<defs>
								<clipPath id="clip0_6658_6357">
									<rect width="13.835" height="16" fill="white" />
								</clipPath>
							</defs>
						</svg>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>
